/**
 * What is wrong with a business rule, in the platform's own terms.
 *
 * Port of the prototype's `ruleProblems` (`studio/type-rule.jsx:121`). A problem
 * that `blocks` is one the engine cannot apply at all — the rule would be stored
 * and never fire, or fire against every sphere. Those are what the composer counts
 * on the row ("{{count}} to fix") and in the section head ("needs fixing"); the
 * advisory ones are guidance the rule editor shows, and are never counted.
 *
 * Every problem carries a translation key and its params rather than a sentence:
 * the composer renders them, and a hardcoded English string here would be
 * unreachable to `t()`.
 */
import type {
  BusinessRule,
  BusinessRuleCondition,
  BusinessRuleExpression,
  BusinessRuleOperator,
} from 'types/businessRules';
import type { SchemaProperty } from 'types/schema';

export interface RuleProblem {
  /** `true` when the engine cannot apply the rule as written. */
  blocks: boolean;
  /** i18n key under `studio.typeEditor.businessRules.problem`. */
  key: string;
  params?: Record<string, unknown>;
}

/** How much an expression may carry. Mirrors `BR_OPERATORS` in the prototype. */
type Arity = 'none' | 'list' | 'either' | 'value';

const ARITY: Record<BusinessRuleOperator, Arity> = {
  equals: 'either',
  not_equals: 'either',
  greater_than: 'either',
  less_than: 'either',
  greater_than_or_equal: 'either',
  less_than_or_equal: 'either',
  contains: 'value',
  exists: 'none',
  is_true: 'none',
  is_false: 'none',
  in: 'list',
  not_in: 'list',
};

/** Relational operators: they return false — not "skip" — when either side is unset. */
const COMPARATIVE: BusinessRuleOperator[] = [
  'greater_than',
  'less_than',
  'greater_than_or_equal',
  'less_than_or_equal',
];

export function operatorArity(operator: BusinessRuleOperator): Arity {
  return ARITY[operator] ?? 'either';
}

export function isComparative(operator: BusinessRuleOperator): boolean {
  return COMPARATIVE.includes(operator);
}

function isCondition(
  node: BusinessRuleExpression | BusinessRuleCondition
): node is BusinessRuleCondition {
  return Array.isArray((node as BusinessRuleCondition).expressions);
}

const propertyOf = (properties: SchemaProperty[], name: string) =>
  properties.find((p) => p.name === name);

const propertyLabel = (properties: SchemaProperty[], name: string) =>
  propertyOf(properties, name)?.label || name;

/**
 * Can a rule read this property at all?
 *
 * An edge property is computed per assignment rather than held on the sphere, so a
 * rule reading one always finds nothing. The refusal says what the author can act
 * on — it is computed — not where the platform keeps it.
 */
export function propertyEligibility(property: SchemaProperty | undefined): {
  ok: boolean;
  whyKey?: string;
} {
  if (!property) return { ok: false, whyKey: 'notOnTypeAnyMore' };
  if (property.defined_on === 'edge') return { ok: false, whyKey: 'computedPerAssignment' };
  return { ok: true };
}

/** The property's allowed set, when it declares one — from `metadata.options` or a `oneOf` rule. */
export function allowedValues(property: SchemaProperty | undefined): unknown[] | null {
  const options = property?.metadata?.options;
  if (Array.isArray(options) && options.length) {
    return options.map((o) => (o && typeof o === 'object' && 'value' in o ? o.value : o));
  }
  const oneOf = (property?.rules ?? []).find((r) => r?.method === 'oneOf');
  if (!oneOf) return null;
  return Array.isArray(oneOf.args?.[0]) ? oneOf.args[0] : oneOf.args;
}

/** Does `applyWhen` already assert that both sides of this expression are set? */
function guardCovers(
  applyWhen: BusinessRuleCondition | undefined,
  expression: BusinessRuleExpression
): boolean {
  const guarded: string[] = [];
  const walk = (node?: BusinessRuleCondition) =>
    (node?.expressions ?? []).forEach((entry) => {
      if (isCondition(entry)) walk(entry);
      else if (entry.operator === 'exists') guarded.push(entry.property);
    });
  walk(applyWhen);
  if (!guarded.includes(expression.property)) return false;
  if (expression.compareToProperty && !guarded.includes(expression.compareToProperty)) return false;
  return true;
}

export function ruleProblems(rule: BusinessRule, properties: SchemaProperty[]): RuleProblem[] {
  const out: RuleProblem[] = [];
  const add = (blocks: boolean, key: string, params?: Record<string, unknown>) =>
    out.push({ blocks, key, params });

  const walk = (tree: BusinessRuleCondition | undefined, depth: number, where: string) => {
    if (!tree) return;
    (tree.expressions ?? []).forEach((entry) => {
      if (isCondition(entry)) {
        // Two levels is the engine's limit; a third can be authored and never run.
        if (depth >= 1) add(true, 'nestsTooDeep');
        walk(entry, depth + 1, where);
        return;
      }
      const arity = operatorArity(entry.operator);
      const property = propertyOf(properties, entry.property);
      const eligible = propertyEligibility(property);

      if (!entry.property) {
        add(true, 'noPropertyChosen', { where });
        return;
      }
      if (!eligible.ok) {
        add(true, 'propertyUnusable', { property: entry.property, whyKey: eligible.whyKey });
        return;
      }
      if (entry.compareToProperty) {
        const other = propertyOf(properties, entry.compareToProperty);
        const otherEligible = propertyEligibility(other);
        if (!otherEligible.ok) {
          add(true, 'propertyUnusable', {
            property: entry.compareToProperty,
            whyKey: otherEligible.whyKey,
          });
          return;
        }
        if (entry.compareToProperty === entry.property) {
          add(true, 'comparedWithItself', { property: propertyLabel(properties, entry.property) });
        }
        if (arity === 'list' || arity === 'none') {
          add(true, 'operatorTakesValueNotProperty', { operator: entry.operator });
        }
      }
      if (arity === 'none' && entry.value !== undefined && entry.value !== '') {
        add(true, 'operatorTakesNoValue', { operator: entry.operator });
      }
      if (arity === 'list') {
        if (!Array.isArray(entry.value) || !entry.value.length) {
          add(true, 'operatorNeedsAValue', { operator: entry.operator });
        } else {
          const universe = allowedValues(property);
          if (universe) {
            const stray = entry.value.filter((v: unknown) => !universe.includes(v));
            // A value outside the property's own allowed set can never reach the engine —
            // per-property validation rejects it first, so the rule can never fire.
            if (stray.length) {
              add(true, 'valueOutsideAllowedSet', {
                property: propertyLabel(properties, entry.property),
                stray: stray.join(', '),
                universe: universe.join(', '),
              });
            }
          }
        }
      }
      if (
        isComparative(entry.operator) &&
        !entry.compareToProperty &&
        (entry.value === undefined || entry.value === '')
      ) {
        add(true, 'operatorNeedsSomethingToCompare', { operator: entry.operator });
      }
      // The create-time trap. Relational operators return false — not "skip" — when
      // either side is unset, so an unguarded rule rejects every fresh sphere.
      if (isComparative(entry.operator)) {
        const other = entry.compareToProperty
          ? propertyOf(properties, entry.compareToProperty)
          : null;
        const bothRequired = !!property?.required && (!entry.compareToProperty || !!other?.required);
        if (!bothRequired && !guardCovers(rule.applyWhen, entry)) {
          add(false, 'rejectsUnsetSides', {
            property: propertyLabel(properties, entry.property),
            operator: entry.operator,
            other: entry.compareToProperty
              ? propertyLabel(properties, entry.compareToProperty)
              : null,
          });
        }
      }
    });
  };

  if (!rule.name || !rule.name.trim()) add(true, 'needsAName');
  if (!(rule.condition?.expressions ?? []).length) add(true, 'requiresNothing');
  if (!(rule.executeOn ?? []).length) add(true, 'needsAMoment');
  if (rule.flowStage && !(rule.executeOn ?? []).includes('flow_transition')) {
    add(true, 'stageWithoutTransition');
  }
  walk(rule.condition, 0, 'requires');
  walk(rule.applyWhen, 0, 'onlyWhen');

  // A single expression against a literal is a property constraint wearing a rule's
  // coat — it belongs on the property, where it travels with it.
  const expressions = rule.condition?.expressions ?? [];
  const only = expressions.length === 1 ? expressions[0] : null;
  if (
    only &&
    !isCondition(only) &&
    !only.compareToProperty &&
    !rule.applyWhen?.expressions &&
    operatorArity(only.operator) !== 'none'
  ) {
    add(false, 'belongsOnTheProperty');
  }
  return out;
}

/** How many problems the engine cannot get past — the number the composer counts. */
export function blockingProblemCount(rule: BusinessRule, properties: SchemaProperty[]): number {
  return ruleProblems(rule, properties).filter((p) => p.blocks).length;
}
